'use client'

import { useEffect, useRef } from 'react'
import { readIsLoggedIn, useSessionContext } from '@/app/context/session'
import { NOT_AUTHENTICATED, SESSION_STATE_COOKIE } from '@/app/lib/constants'
import { getCookie } from '@/app/lib/utils'
import { CHANGED_GRACE_DELAY_MS, POLL_INTERVAL_MS } from './constants'
import type { SessionCheckerProps } from './types'

/**
 * Comprobación silenciosa de sesión, sin salida visual (montado una vez en el
 * layout raíz). Reimplementa el patrón OIDC Session Management del archivo de
 * referencia PRO aportado — ver
 * `specs/007-header-login-session/contracts/session-check-protocol.md` — con
 * un único iframe oculto (el original duplicaba el iframe; no se replica) y
 * sin detener el polling tras el primer cambio detectado (aquí sí debe seguir
 * activo, para reflejar cambios de sesión posteriores durante la misma
 * visita — FR-004, SC-003).
 */
export function SessionChecker({ checkSessionIframeUrl, opOrigin, clientId, rpOrigin }: SessionCheckerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const { setIsLoggedIn } = useSessionContext()

  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe) return

    // Si el navegador bloquea las cookies de terceros, el documento del OP no
    // puede leer su propia cookie de sesión y nunca responde 'changed'/'unchanged'
    // a este postMessage — no es un caso a manejar aquí: el sondeo sigue
    // reintentando sin bloquear nada, y el estado de sesión simplemente se
    // queda en su último valor conocido (ya resuelto en servidor al cargar la
    // página, sin depender de este iframe). Ver
    // specs/009-lighthouse-buenas-practicas/contracts/session-check-degraded-mode.md.
    const tick = () => {
      const contentWindow = iframe.contentWindow
      if (!contentWindow) return
      const sessionState = getCookie(SESSION_STATE_COOKIE) ?? NOT_AUTHENTICATED
      contentWindow.postMessage(`${clientId} ${sessionState}`, opOrigin)
    }

    // Validación obligatoria del mensaje entrante (regla de seguridad del
    // proyecto — "Validate all boundary input"): origen exacto del OP y que
    // provenga del iframe que este componente montó, nunca `'*'` ni un origen
    // sin comprobar.
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== opOrigin) return
      if (event.source !== iframe.contentWindow) return
      if (event.data !== 'changed') return

      window.setTimeout(() => {
        setIsLoggedIn(readIsLoggedIn())
      }, CHANGED_GRACE_DELAY_MS)
    }

    const intervalId = window.setInterval(tick, POLL_INTERVAL_MS)
    window.addEventListener('message', handleMessage)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener('message', handleMessage)
    }
  }, [clientId, opOrigin, setIsLoggedIn])

  const src = `${checkSessionIframeUrl}?origin=${encodeURIComponent(rpOrigin)}`

  return (
    <iframe
      ref={iframeRef}
      src={src}
      title="Comprobación de sesión"
      aria-hidden="true"
      tabIndex={-1}
      width={0}
      height={0}
      frameBorder={0}
    />
  )
}
